import { copy, equals, IPromise, IQService, IDeferred } from "angular";

import { IWhiteboardState, IWhiteboard } from "../../models";
import { IWhiteboardShapeState, reservedImportedShapeFields, isImportedType } from "../shapes";
import { IWhiteboardLinkState, linkId } from "../links";
import { WhiteboardsService } from "@gtmhub/whiteboards";
import { IWhiteboardRenderer, IWhiteboardRenderOptions } from "../whiteboard-renderer";

/**
 * For imported shapes we only want to store some reserved properties,
 * because the complete model is loaded async when rendered
 */
const purify = (state: IWhiteboardState): IWhiteboardState => {
  for (const shape of state.shapes) {
    if (isImportedType(shape.type)) {
      for (const key of Object.keys(shape)) {
        if (!reservedImportedShapeFields.includes(key as keyof IWhiteboardShapeState)) {
          delete shape[key];
        }
      }
    }
  }

  return state;
};

/**
 * Replace alert/async shapes with their original shapes
 */
const replaceWithOriginalShapes = (state: IWhiteboardState) => {
  for (let i = 0; i < state.shapes.length; i++) {
    const shape = state.shapes[i];
    if ((shape.type === "alert" && shape.original) || shape.type === "async") {
      state.shapes[i] = { ...shape.original, position: shape.position };
    }
  }

  // remove alert shapes without `original` one attached to them
  state.shapes = state.shapes.filter((shape) => shape.type !== "alert");
};

type MinMaxZIndices = [number, number];

export class WhiteboardStateHolder {
  private state: { version: string; shapes: Record<string, IWhiteboardShapeState>; links: Record<string, IWhiteboardLinkState> };
  private isDirty: boolean;
  private whiteboard: IWhiteboard;
  private loadedDeferred: IDeferred<void>;

  static $inject = ["$q", "WhiteboardsService"];

  constructor(private $q: IQService, private whiteboardsService: WhiteboardsService) {
    this.unload();
  }

  get loaded(): IPromise<void[]> {
    // in order to reset Zone of this.loadedDeferred.promise we have to resolve the promise with $q service
    return this.$q.all([this.loadedDeferred.promise]);
  }

  get whiteboardId(): string {
    return this.whiteboard && this.whiteboard.id;
  }

  get dirty(): boolean {
    return this.isDirty;
  }

  markStateAsDirty(): void {
    this.isDirty = true;
  }

  load(whiteboard: IWhiteboard, state: IWhiteboardState): void {
    if (this.isLoaded()) {
      this.unload();
    }

    this.whiteboard = whiteboard;
    this.state = {
      version: state.version,
      shapes: state.shapes.reduce((map, shape) => Object.assign(map, { [shape.id]: copy(shape) }), {}),
      links: state.links.reduce((map, link) => Object.assign(map, { [linkId(link)]: copy(link) }), {}),
    };

    this.loadedDeferred.resolve();
  }

  unload(): void {
    delete this.state;
    delete this.whiteboard;
    this.isDirty = false;

    this.loadedDeferred = this.$q.defer<void>();
  }

  findShape<T extends IWhiteboardShapeState>(filter: ((shape: IWhiteboardShapeState) => unknown) | string): T | undefined {
    const shape = typeof filter === "string" ? this.state.shapes[filter] : Object.values(this.state.shapes).find((s) => filter(s));
    if (shape) {
      return copy(shape) as T;
    }
  }

  findShapes(filter: (shape: IWhiteboardShapeState) => unknown): IWhiteboardShapeState[] {
    return copy(Object.values(this.state.shapes).filter((shape) => filter(shape)));
  }

  findLinks(filter: (link: IWhiteboardLinkState) => unknown): IWhiteboardLinkState[] {
    return copy(Object.values(this.state.links).filter((link) => filter(link)));
  }

  findShapeLinks(shapeId: string): IWhiteboardLinkState[] {
    return this.findLinks((link) => link.source.id === shapeId || link.target.id === shapeId);
  }

  applyShapeChanges(shapeId: string, changes: Record<string, unknown>): this {
    if (!(shapeId in this.state.shapes)) {
      throw new Error(`No such shape with ID ${shapeId}`);
    }

    const updatedShape = Object.assign({}, this.state.shapes[shapeId], copy(changes));
    return this.updateShape(updatedShape);
  }

  updateShape(...shapes: IWhiteboardShapeState[]): this {
    this.ensureStateLoaded();

    for (const shape of shapes) {
      if (!equals(this.state.shapes[shape.id], shape)) {
        this.state.shapes[shape.id] = copy(shape);
      }
    }
    return this;
  }

  deleteShape(...shapeIds: string[]): this {
    this.ensureStateLoaded();

    for (const shapeId of shapeIds) {
      if (shapeId in this.state.shapes) {
        delete this.state.shapes[shapeId];
      }
    }
    return this;
  }

  updateLink(...links: IWhiteboardLinkState[]): this {
    this.ensureStateLoaded();

    for (const link of links) {
      const id = linkId(link);
      if (!equals(this.state.links[id], link)) {
        this.state.links[linkId(link)] = copy(link);
      }
    }
    return this;
  }

  deleteLink(...links: IWhiteboardLinkState[]): this {
    this.ensureStateLoaded();

    for (const link of links) {
      const id = linkId(link);
      if (id in this.state.links) {
        delete this.state.links[id];
      }
    }
    return this;
  }

  updatePicture(canvasDataUrl: string): IPromise<unknown> {
    return this.whiteboardsService.updatePicture(this.whiteboard, canvasDataUrl).finally(() => (this.isDirty = false));
  }

  render(renderer: IWhiteboardRenderer, options?: IWhiteboardRenderOptions): void {
    renderer.render(this.toWhiteboardState(), options);
  }

  renderSingleShape(renderer: IWhiteboardRenderer, shapeId: string, options?: IWhiteboardRenderOptions): void {
    renderer.renderSingleShape(this.toWhiteboardState(), shapeId, options);
  }

  exportState(): IWhiteboardState {
    this.ensureStateLoaded();

    const state = this.toWhiteboardState();
    replaceWithOriginalShapes(state);
    return purify(state);
  }

  calculateNextZIndex(): number {
    this.ensureStateLoaded();
    let zIndex = 0;

    const shapes = this.findShapes((s) => s);
    zIndex = shapes.reduce((acc, x) => (acc === undefined || x.zIndex > acc ? x.zIndex : acc), -1) + 1;

    return zIndex;
  }

  getMinMaxZIndices(): MinMaxZIndices {
    const shapes = this.findShapes((s) => s);
    return shapes.reduce(
      (acc, x) => {
        acc[0] = acc[0] === undefined || x.zIndex < acc[0] ? x.zIndex : acc[0];
        acc[1] = acc[1] === undefined || x.zIndex > acc[1] ? x.zIndex : acc[1];
        return acc;
      },
      [undefined, undefined]
    );
  }

  private toWhiteboardState(): IWhiteboardState {
    this.ensureStateLoaded();

    return {
      version: this.state.version,
      shapes: copy(Object.values(this.state.shapes)),
      links: copy(Object.values(this.state.links)),
    };
  }

  private isLoaded() {
    return !!this.state;
  }

  private ensureStateLoaded() {
    if (!this.isLoaded()) {
      throw new Error("You must load state first");
    }
  }
}
